import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { pluralizeWithCount } from "@common/helpers/string";
import { ArrowCornerIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { createTransactionsUrl } from "@components/transactions/lib/transactionsUrl";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import {
    AuditLogDiff,
    HistoryDetailsItem,
    isHistoryDetailsResponse,
} from "@common/types/auditLog";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import { View } from "@common/types/views/views";
import { HistoryGroupBy } from "@common/constants/history-source-types";
import { StepTrailBreadcrumbs } from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { historyDrawerContext } from "../../historyDrawerContext";
import { getShowRevertedParam } from "../../views/historyViewConfig";
import { groupByKey } from "../../utils/groupByKey";
import { getBatchKey } from "../../utils/getBatchKey";
import { getActivityDescription } from "../../utils/getActivityDescription";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import {
    createSplitParentTransactionsViewConfig,
    SplitParentTransactionRow,
} from "./views/splitParentTransactionsViewConfig";
import {
    createSplitVersionsViewConfig,
    SplitVersionRow,
} from "./views/splitVersionsViewConfig";
import {
    createSplitDetailsViewConfig,
    SplitDetailRow,
} from "./views/splitDetailsViewConfig";
import {
    createDefaultSplitParentTransactionsView,
    createDefaultSplitVersionsView,
    createDefaultSplitDetailsView,
    withGlFirstSplitLedgerColumn,
} from "./views/defaultSplitViews";
import { splitTransactionDataSchema } from "./schema";
import styles from "./SplitHistoryDrawer.module.scss";

const SPLIT_BREADCRUMB_STEPS = [
    { id: "parent" as const, label: "Split transactions" },
    { id: "versions" as const, label: "Versions" },
    { id: "details" as const, label: "Details" },
];

interface CollectRawValuesProps {
    resource: Record<string, unknown> | undefined;
    diffs: AuditLogDiff[];
    fallbackDate: Date | string;
}

function collectRawValues({
    resource,
    diffs,
    fallbackDate,
}: CollectRawValuesProps): Record<string, unknown> {
    const raw: Record<string, unknown> = { date: fallbackDate };
    const fields = splitTransactionDataSchema.keyof().options;

    for (const field of fields) {
        const resourceVal = resource?.[field];
        const diff = diffs.find((d) => d.field === field);
        const diffVal = diff?.after?.value ?? diff?.before?.value;

        if (diffVal != null) {
            raw[field] = diffVal;
            continue;
        }

        if (resourceVal != null) {
            raw[field] = resourceVal;
        }
    }
    return raw;
}

interface ExtractTransactionDataOptions {
    preferSplitDescription?: boolean;
}

function extractTransactionData(
    log: HistoryDetailsItem,
    options?: ExtractTransactionDataOptions,
) {
    const diffs = log.diff ?? [];
    const raw = collectRawValues({
        resource: log.resource,
        diffs,
        fallbackDate: log.createdAt,
    });
    const parsed = splitTransactionDataSchema.parse(raw);

    const description =
        (options?.preferSplitDescription
            ? parsed.splitDescription
            : undefined) ??
        (parsed.bankDescription ||
            (raw.splitDescription as string) ||
            (raw.bankDescription as string) ||
            parsed.displayDescription) ??
        "";

    const amount =
        parsed.amount !== 0 ? parsed.amount : ((raw.amount as number) ?? 0);
    const originalAmount =
        parsed.originalAmount !== 0
            ? parsed.originalAmount
            : ((raw.originalAmount as number) ?? 0);

    return {
        date: parsed.date,
        bankDescription: description,
        category: parsed.categoryId
            ? { id: parsed.categoryId, label: parsed.categoryLabel }
            : {},
        ledger: {
            accountId: parsed.accountId ?? undefined,
            accountLabel: parsed.accountLabel,
        },
        amount,
        originalAmount,
        entity: {},
        entityId: log.entityId ?? parsed.entityId ?? null,
        financialAccountId: parsed.financialAccountId ?? null,
    };
}

export const SplitHistoryDrawer: React.FC = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);
    const { enabled: isGlFirst } = useIsGlFirst();
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const parentConfig = useMemo(
        () => createSplitParentTransactionsViewConfig(isGlFirst),
        [isGlFirst],
    );
    const versionsConfig = useMemo(
        () => createSplitVersionsViewConfig(isGlFirst),
        [isGlFirst],
    );
    const detailsConfig = useMemo(
        () => createSplitDetailsViewConfig(isGlFirst),
        [isGlFirst],
    );

    const [isOpen, setIsOpen] = useState(false);
    const [parentSearch, setParentSearch] = useState("");
    const [versionsSearch, setVersionsSearch] = useState("");
    const [detailsSearch, setDetailsSearch] = useState("");

    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
        null,
    );
    const [selectedBatchKey, setSelectedBatchKey] = useState<string | null>(
        null,
    );

    const [parentView, setParentView] = useState<View>(() =>
        createDefaultSplitParentTransactionsView(isGlFirst),
    );
    const [versionsView, setVersionsView] = useState<View>(() =>
        createDefaultSplitVersionsView(isGlFirst),
    );
    const [detailsView, setDetailsView] = useState<View>(() =>
        createDefaultSplitDetailsView(isGlFirst),
    );

    const normalizedParentView = useMemo(
        () => withGlFirstSplitLedgerColumn(parentView, isGlFirst),
        [parentView, isGlFirst],
    );
    const normalizedVersionsView = useMemo(
        () => withGlFirstSplitLedgerColumn(versionsView, isGlFirst),
        [versionsView, isGlFirst],
    );
    const normalizedDetailsView = useMemo(
        () => withGlFirstSplitLedgerColumn(detailsView, isGlFirst),
        [detailsView, isGlFirst],
    );

    const [parentPage, setParentPage] = useState(1);
    const [versionsPage, setVersionsPage] = useState(1);

    const shouldShowContent = isOpen || open;

    const isFieldGrouped =
        historyRow?.historyItem.groupBy === HistoryGroupBy.FIELD;

    const { data: allLogsResponse, isLoading: isParentsLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: isFieldGrouped
                ? undefined
                : historyRow?.historyItem.sourceIds,
            fields: isFieldGrouped ? historyRow?.fields : undefined,
            changeTypes: isFieldGrouped ? historyRow?.changeTypes : undefined,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            page: parentPage,
            limit: 100,
            showReverted: getShowRevertedParam(historyRow),
            options: {
                enabled:
                    shouldShowContent &&
                    !!historyRow &&
                    !!historyRow.sourceType,
            },
        });

    const allLogs = useMemo<HistoryDetailsItem[]>(() => {
        if (!allLogsResponse || !isHistoryDetailsResponse(allLogsResponse)) {
            return [];
        }
        return allLogsResponse.data;
    }, [allLogsResponse]);

    const parentTotalPages = useMemo(() => {
        if (!allLogsResponse || !isHistoryDetailsResponse(allLogsResponse)) {
            return 1;
        }
        return allLogsResponse.totalPages;
    }, [allLogsResponse]);

    const logsByGroupKey = useMemo(
        () =>
            groupByKey(allLogs, (log) => {
                const splitParentDiff = log.diff?.find(
                    (d) => d.field === "splitParentId",
                );
                const splitParentId =
                    splitParentDiff?.after?.value ??
                    log.resource?.splitParentId;

                return String(
                    splitParentId ??
                        (isFieldGrouped ? log.resourceId : null) ??
                        log.sourceId ??
                        "unknown",
                );
            }),
        [allLogs, isFieldGrouped],
    );

    const parentRows = useMemo<SplitParentTransactionRow[]>(() => {
        const rows: SplitParentTransactionRow[] = [];
        for (const [groupKey, logs] of logsByGroupKey.entries()) {
            if (groupKey === "unknown") {
                continue;
            }
            const parent =
                logs.find((log) => log.resource?.splitParentId === null) ??
                logs[0];
            const parentData = extractTransactionData(parent);
            rows.push({
                id: groupKey,
                sourceId: groupKey,
                ...parentData,
            });
        }
        return rows;
    }, [logsByGroupKey]);

    const filteredParentRows = useMemo(() => {
        if (!parentSearch) {
            return parentRows;
        }
        const term = parentSearch.toLowerCase();
        return parentRows.filter(
            (row) =>
                row.bankDescription.toLowerCase().includes(term) ||
                (row.category?.label as string | undefined)
                    ?.toLowerCase()
                    .includes(term),
        );
    }, [parentRows, parentSearch]);

    const { data: versionsResponse, isLoading: isVersionsApiLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: selectedSourceId ? [selectedSourceId] : undefined,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            page: versionsPage,
            limit: 100,
            showReverted: getShowRevertedParam(historyRow),
            options: {
                enabled:
                    shouldShowContent &&
                    !!selectedSourceId &&
                    !!historyRow &&
                    !isFieldGrouped,
            },
        });

    const isVersionsLoading = isFieldGrouped ? false : isVersionsApiLoading;

    const versionGroups = useMemo(() => {
        if (isFieldGrouped && selectedSourceId) {
            const logsForGroup = logsByGroupKey.get(selectedSourceId) ?? [];
            return groupByKey(logsForGroup, (log) =>
                getBatchKey(log.createdAt),
            );
        }
        if (!versionsResponse || !isHistoryDetailsResponse(versionsResponse)) {
            return new Map<string, HistoryDetailsItem[]>();
        }
        return groupByKey(versionsResponse.data, (log) =>
            getBatchKey(log.createdAt),
        );
    }, [isFieldGrouped, selectedSourceId, logsByGroupKey, versionsResponse]);

    const selectedParent = useMemo(
        () => parentRows.find((r) => r.sourceId === selectedSourceId),
        [parentRows, selectedSourceId],
    );

    const versionRows = useMemo<SplitVersionRow[]>(() => {
        const rows: SplitVersionRow[] = [];
        for (const [batchKey, logs] of versionGroups.entries()) {
            const firstLog = logs[0];
            rows.push({
                id: batchKey,
                batchKey,
                date: new Date(firstLog.createdAt),
                splitsCount: logs.length,
                bankDescription: selectedParent?.bankDescription ?? "",
                category: selectedParent?.category ?? {},
                ledger: selectedParent?.ledger ?? {},
                amount: selectedParent?.amount ?? 0,
                originalAmount: selectedParent?.originalAmount ?? 0,
                entity: selectedParent?.entity ?? {},
                entityId: selectedParent?.entityId ?? null,
            });
        }
        return rows.toSorted((a, b) => b.date.getTime() - a.date.getTime());
    }, [versionGroups, selectedParent]);

    const filteredVersionRows = useMemo(() => {
        if (!versionsSearch) {
            return versionRows;
        }
        const term = versionsSearch.toLowerCase();
        return versionRows.filter(
            (row) =>
                row.bankDescription.toLowerCase().includes(term) ||
                String(row.splitsCount).includes(term),
        );
    }, [versionRows, versionsSearch]);

    const versionsTotalPages = useMemo(() => {
        if (isFieldGrouped) {
            return 1;
        }
        if (!versionsResponse || !isHistoryDetailsResponse(versionsResponse)) {
            return 1;
        }
        return versionsResponse.totalPages;
    }, [isFieldGrouped, versionsResponse]);

    const shouldSkipVersions = useMemo(() => {
        if (isVersionsLoading) {
            return false;
        }
        if (versionsTotalPages > 1) {
            return false;
        }
        return versionRows.length === 1;
    }, [isVersionsLoading, versionsTotalPages, versionRows.length]);

    useEffect(() => {
        if (
            selectedSourceId &&
            !selectedBatchKey &&
            shouldSkipVersions &&
            versionRows.length > 0
        ) {
            setSelectedBatchKey(versionRows[0].batchKey);
        }
    }, [selectedSourceId, selectedBatchKey, shouldSkipVersions, versionRows]);

    const selectedVersionLogs = useMemo<HistoryDetailsItem[]>(() => {
        if (!selectedBatchKey) {
            return [];
        }
        return versionGroups.get(selectedBatchKey) ?? [];
    }, [selectedBatchKey, versionGroups]);

    const detailRows = useMemo<SplitDetailRow[]>(
        () =>
            selectedVersionLogs.map((log) => {
                const data = extractTransactionData(log, {
                    preferSplitDescription: true,
                });
                return {
                    id: log.resourceId ?? log.id,
                    ...data,
                    financialAccountId:
                        data.financialAccountId ??
                        selectedParent?.financialAccountId ??
                        null,
                };
            }),
        [selectedVersionLogs, selectedParent],
    );

    const filteredDetailRows = useMemo(() => {
        if (!detailsSearch) {
            return detailRows;
        }
        const term = detailsSearch.toLowerCase();
        return detailRows.filter(
            (row) =>
                row.bankDescription.toLowerCase().includes(term) ||
                (row.category?.label as string | undefined)
                    ?.toLowerCase()
                    .includes(term),
        );
    }, [detailRows, detailsSearch]);

    const handleClose = useCallback(() => {
        setParentSearch("");
        setVersionsSearch("");
        setDetailsSearch("");
        setSelectedSourceId(null);
        setSelectedBatchKey(null);
        setParentPage(1);
        setVersionsPage(1);
        closeDrawer();
    }, [closeDrawer]);

    const handleSelectParent = useCallback(
        (row?: SplitParentTransactionRow) => {
            if (!row) {
                return;
            }
            setSelectedSourceId(row.sourceId);
            setVersionsSearch("");
            setVersionsPage(1);
        },
        [],
    );

    const handleCloseVersions = useCallback(() => {
        setSelectedSourceId(null);
        setVersionsSearch("");
        setVersionsPage(1);
    }, []);

    const handleSelectVersion = useCallback((row?: SplitVersionRow) => {
        if (!row) {
            return;
        }
        setSelectedBatchKey(row.batchKey);
        setDetailsSearch("");
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedBatchKey(null);
        setDetailsSearch("");
        if (shouldSkipVersions) {
            setSelectedSourceId(null);
            setVersionsSearch("");
            setVersionsPage(1);
        }
    }, [shouldSkipVersions]);

    const handleBreadcrumbStepChange = useCallback(
        (stepId: "parent" | "versions" | "details") => {
            if (stepId === "parent") {
                setSelectedSourceId(null);
                setSelectedBatchKey(null);
                setVersionsSearch("");
                setVersionsPage(1);
                setDetailsSearch("");
            } else if (stepId === "versions") {
                handleCloseDetails();
            }
        },
        [handleCloseDetails],
    );

    const detailsBreadcrumbSteps = useMemo(() => {
        if (shouldSkipVersions) {
            return [SPLIT_BREADCRUMB_STEPS[0], SPLIT_BREADCRUMB_STEPS[2]];
        }
        return SPLIT_BREADCRUMB_STEPS;
    }, [shouldSkipVersions]);

    const layer1HeaderAddonLeft = useMemo(() => {
        if (!historyRow) {
            return null;
        }

        const description = getActivityDescription(historyRow);

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={description}
            />
        );
    }, [historyRow]);

    const navigate = useStateAwareNavigate();

    const parsedTransactionId = useMemo(() => {
        if (!selectedSourceId) {
            return null;
        }
        const id = parseInt(selectedSourceId, 10);
        return isNaN(id) ? null : id;
    }, [selectedSourceId]);

    const handleViewTransaction = useCallback(() => {
        if (!parsedTransactionId) {
            return;
        }
        navigate(
            createTransactionsUrl({ transactionIds: [parsedTransactionId] }),
            { state: { openSplit: true } },
        );
    }, [parsedTransactionId, navigate]);

    const viewOriginalButton = useMemo(() => {
        if (!parsedTransactionId) {
            return undefined;
        }
        return (
            <Tooltip content="View transaction" side="bottom">
                <Button
                    iconOnly
                    variant="primary"
                    size="md"
                    onClick={handleViewTransaction}
                    aria-label="View transaction"
                >
                    <ArrowCornerIcon />
                </Button>
            </Tooltip>
        );
    }, [parsedTransactionId, handleViewTransaction]);

    const renderLayer1Content = () => {
        if (isParentsLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredParentRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            parentSearch
                                ? "No matching transactions found"
                                : "No split transactions found"
                        }
                    />
                </div>
            );
        }

        const footer = (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.footerLeft}>
                    {parentTotalPages > 1 && (
                        <Pagination
                            size="md"
                            currentPage={parentPage}
                            pageCount={parentTotalPages}
                            onPageChange={setParentPage}
                        />
                    )}
                    <CommandIslandTotal>
                        {pluralizeWithCount("log", filteredParentRows.length)}
                    </CommandIslandTotal>
                </div>
                <div />
            </CommandIsland>
        );

        return (
            <ViewTable
                data={filteredParentRows}
                view={normalizedParentView}
                config={parentConfig}
                onViewChange={setParentView}
                onRowClick={handleSelectParent}
                emptyView={() => <EmptyState header="No transactions found" />}
                footer={footer}
                className={styles.historyDetailsView}
            />
        );
    };

    const renderLayer2Content = () => {
        if (isVersionsLoading || (shouldSkipVersions && !selectedBatchKey)) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredVersionRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            versionsSearch
                                ? "No matching versions found"
                                : "No split versions found"
                        }
                    />
                </div>
            );
        }

        const showVersionsPagination = versionsTotalPages > 1;
        const footer = (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.footerLeft}>
                    {showVersionsPagination && (
                        <Pagination
                            size="md"
                            currentPage={versionsPage}
                            pageCount={versionsTotalPages}
                            onPageChange={setVersionsPage}
                        />
                    )}
                    <CommandIslandTotal>
                        {pluralizeWithCount("log", filteredVersionRows.length)}
                    </CommandIslandTotal>
                </div>
                <div>{viewOriginalButton}</div>
            </CommandIsland>
        );

        return (
            <ViewTable
                data={filteredVersionRows}
                view={normalizedVersionsView}
                config={versionsConfig}
                onViewChange={setVersionsView}
                onRowClick={handleSelectVersion}
                emptyView={() => <EmptyState header="No versions found" />}
                footer={footer}
                className={styles.historyDetailsView}
            />
        );
    };

    const renderLayer3Content = () => {
        if (filteredDetailRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            detailsSearch
                                ? "No matching splits found"
                                : "No split details found"
                        }
                    />
                </div>
            );
        }

        const footer = (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <CommandIslandTotal>
                    {pluralizeWithCount("log", filteredDetailRows.length)}
                </CommandIslandTotal>
                <div>{viewOriginalButton}</div>
            </CommandIsland>
        );

        return (
            <ViewTable
                data={filteredDetailRows}
                view={normalizedDetailsView}
                config={detailsConfig}
                onViewChange={setDetailsView}
                emptyView={() => <EmptyState header="No split details found" />}
                footer={footer}
                className={styles.historyDetailsView}
            />
        );
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={handleClose}
                direction="right"
                onAnimationEnd={setIsOpen}
                headerAddonLeft={layer1HeaderAddonLeft}
                size="fullScreenWithNav"
                noPadding
            >
                {shouldShowContent && historyRow && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={parentSearch}
                                    onChange={(v) => setParentSearch(v ?? "")}
                                    placeholder="Search..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderLayer1Content()}
                        </div>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedSourceId}
                onClose={handleCloseVersions}
                direction="right"
                size="fullScreenWithNav"
                noPadding
                headerAddonLeft={
                    <StepTrailBreadcrumbs
                        steps={SPLIT_BREADCRUMB_STEPS.slice(0, 2)}
                        currentStep="versions"
                        onStepChange={handleBreadcrumbStepChange}
                    />
                }
            >
                {selectedSourceId && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={versionsSearch}
                                    onChange={(v) => setVersionsSearch(v ?? "")}
                                    placeholder="Search..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderLayer2Content()}
                        </div>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedBatchKey}
                onClose={handleCloseDetails}
                direction="right"
                size="fullScreenWithNav"
                noPadding
                headerAddonLeft={
                    <StepTrailBreadcrumbs
                        steps={detailsBreadcrumbSteps}
                        currentStep="details"
                        onStepChange={handleBreadcrumbStepChange}
                    />
                }
            >
                {selectedBatchKey && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={detailsSearch}
                                    onChange={(v) => setDetailsSearch(v ?? "")}
                                    placeholder="Search..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderLayer3Content()}
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};
